import { css } from '@emotion/css';
import { Location } from '../../../shared/types/server-types';
import { h3Style, labelStyle } from '../../../shared/styles/text';
import { Colors } from '../../../shared/styles/colors';
import { useNavigator } from '../../../routing/navigator';
import {
  Motion,
  Radius,
  Shadow,
  unstyledButton,
} from '../../../shared/styles/tokens';

interface ILocationCardProps {
  location: Location;
}

export const LocationCard = (props: ILocationCardProps): JSX.Element => {
  const { location } = props;
  const navigator = useNavigator();

  return (
    <button
      type="button"
      className={containerStyle}
      onClick={() => {
        navigator.toLocation(location.id);
      }}
    >
      <figure className={figureStyle}>
        <div className={imageFrameStyle}>
          <img
            className={imageStyle}
            src={location.thumbnailUrl}
            alt={location.name}
            loading="lazy"
          />
        </div>
        <figcaption className={nameStyle}>{location.name}</figcaption>
        <span className={addressStyle}>
          {location.city}, {location.country}
        </span>
      </figure>
    </button>
  );
};

const containerStyle = css`
  ${unstyledButton}
  display: block;
  width: 100%;
  margin-bottom: 1.75rem;
  border-radius: ${Radius.lg};

  @media (hover: hover) {
    &:hover img {
      transform: scale(1.03);
    }
    &:hover figcaption {
      color: ${Colors.CAPRI_BLUE_DARK};
    }
  }

  &:active img {
    transform: scale(0.99);
  }

  &:focus-visible {
    outline: 2px solid ${Colors.CAPRI_BLUE};
    outline-offset: 4px;
  }
`;

const figureStyle = css`
  display: flex;
  flex-direction: column;
`;

const imageFrameStyle = css`
  width: 100%;
  height: 11.5rem;
  margin-bottom: 0.875rem;
  border-radius: ${Radius.lg};
  overflow: hidden;
  background: ${Colors.SURFACE_MUTED};
  box-shadow: ${Shadow.card};
`;

const imageStyle = css`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 400ms cubic-bezier(0.2, 0, 0, 1);
`;

const nameStyle = css`
  ${h3Style}
  color: ${Colors.CAPRI_BLUE};
  transition: color ${Motion.fast};
`;

const addressStyle = css`
  ${labelStyle}
  margin-top: 0.25rem;
  color: ${Colors.TEXT_SECONDARY};
`;
